/**
 * The currency this deployment CHARGES in — the SYMBOL every money figure needs.
 *
 * ── The bug this exists for ─────────────────────────────────────────────
 *
 * Under the billing-currency-native model every money number the SPA renders is
 * already denominated in the deployment's own currency: caps, plan pools,
 * balances, spend. The amount cannot say which currency it is, and `formatMoney`
 * lives in a dependency-free package that cannot resolve a deployment setting —
 * so its `'USD'` default put a dollar sign in front of Malaysian ringgit.
 *
 * That failure mode is the dangerous kind. The NUMBER is right; only the symbol
 * lies. Nothing errors, nothing looks broken, and a customer reading "$550"
 * where the truth is "RM550" has been told their bill is five and a half times
 * what it is. It is the display half of exactly the class of defect that
 * produced every finding in this migration: a reader trusting a unit it never
 * verified.
 *
 * ── Why it lives in `@papan/core-ui` ────────────────────────────────────
 *
 * `features/orgs` and `features/billing` both render money and neither may
 * import the other — the arrow between them already runs one way, and a
 * provider owned by either would be re-implemented by the other. That is
 * precisely how `Modal`, `ActionDisclosure` and `DirtyBar` came to be
 * duplicated before they moved here. `WriteStatusProvider` is the same shape
 * for the same reason.
 *
 * ── Why it fails to USD rather than to nothing ──────────────────────────
 *
 * A missing currency must not blank a figure or throw: a money screen that
 * renders nothing while a fetch is in flight is worse than one whose symbol
 * arrives a beat late, and `'USD'` is what every one of these call sites
 * already defaulted to before this existed. So the fallback is a strict
 * no-change, and the provider only ever improves on it.
 *
 * The deliberate consequence: with no provider mounted — mobile, a test, a
 * story — every consumer behaves exactly as it did before. Same stance as
 * `DeckVisibility`.
 */
import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import { request } from '@papan/api-client'

/** Lowercase or uppercase ISO-4217; `formatMoney` upper-cases for `Intl`. */
const Ctx = createContext<string>('USD')

/**
 * Fetch the deployment's charging currency once and publish it.
 *
 * ONE fetch for the whole app rather than one per money screen: it is a
 * deployment-wide constant that cannot change while the tab is open (a rate
 * epoch can change the RATE; changing the CURRENCY is a re-denomination, which
 * is what this whole migration is). So there is deliberately no refresh here,
 * and notably no `useRefreshWhenShown` — the keep-alive-deck staleness problem
 * is about figures, not about which currency the deployment bills in.
 */
export function BillingCurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    let alive = true
    request<{ currency: string }>('/api/orgs/billing-currency')
      .then((r) => {
        if (alive && r?.currency) setCurrency(r.currency)
      })
      // Swallowed on purpose. The fallback IS the previous behaviour, so a
      // failed fetch costs a symbol and never a figure; surfacing an error
      // toast on every money screen for that would be worse than the defect.
      .catch(() => {})
    return () => { alive = false }
  }, [])

  return <Ctx.Provider value={currency}>{children}</Ctx.Provider>
}

/** The deployment's charging currency. `'USD'` with no provider above. */
export function useBillingCurrency(): string {
  return useContext(Ctx)
}

/**
 * `formatMoney` options pre-bound to the deployment's currency.
 *
 * Spread it rather than passing `currency` by hand at each of ~57 call sites:
 * a call site that forgets is not a compile error and not a visible one — it
 * just renders the wrong symbol — so the ergonomic path has to be the correct
 * one.
 *
 * `rate` is deliberately absent. Under this model the amount is ALREADY in the
 * billing currency, so any multiplier here is a second conversion; `formatMoney`
 * says the same at its own call site.
 */
export function useMoneyOpts(): { currency: string } {
  const currency = useBillingCurrency()
  return useMemo(() => ({ currency }), [currency])
}
